import { useEffect, useState } from 'react'
import { Loader2, AlertCircle, ShieldCheck } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'

// Per-account RBAC for Secure Share. Strict opt-in: providers have no access until enabled here, and a
// grant can be time-boxed (auto-expires after the chosen number of days).
export default function SecureShareAccessTab() {
  const [providers, setProviders] = useState<api.SecureShareAccessProvider[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [daysById, setDaysById] = useState<Record<string, string>>({})

  function load() {
    setLoading(true)
    api.getSecureShareAccess()
      .then(r => { setProviders(r.providers ?? []); setErr(null) })
      .catch(e => setErr(friendlyError(e)))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  async function allow(p: api.SecureShareAccessProvider) {
    setBusyId(p.id); setErr(null)
    const raw = (daysById[p.id] || '').trim()
    const days = raw ? parseInt(raw, 10) : undefined
    try { await api.grantSecureShareAccess(p.id, days && days > 0 ? days : undefined); load() }
    catch (e) { setErr(friendlyError(e)) } finally { setBusyId(null) }
  }
  async function revoke(p: api.SecureShareAccessProvider) {
    setBusyId(p.id); setErr(null)
    try { await api.revokeSecureShareAccess(p.id); load() }
    catch (e) { setErr(friendlyError(e)) } finally { setBusyId(null) }
  }

  return (
    <div style={{ maxWidth: '100%' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 6px' }}>
        <ShieldCheck size={18} /> Secure Share access
      </h2>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--color-text-soft)', lineHeight: 1.5 }}>
        Secure Share is <strong>off for every provider by default</strong> — they won't see it until you enable it
        here. Leave the days field blank for permanent access, or enter a number of days to grant temporary access
        that automatically expires.
      </p>
      {loading && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 size={14} className="spin" /> Loading…</div>}
      {err && <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 10 }}><AlertCircle size={13} /> {err}</div>}
      {!loading && providers.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>No provider accounts in this pharmacy yet.</div>
      )}
      {providers.map(p => {
        const expired = !!p.expires_at && new Date(p.expires_at).getTime() <= Date.now()
        return (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--color-border, #eee)' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14 }}>{p.display_name || p.email}</div>
              {p.display_name && p.email && <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>{p.email}</div>}
              {p.granted && (
                <div style={{ fontSize: 12, color: '#16a34a' }}>
                  Enabled{p.expires_at ? ` · expires ${formatUsDate(p.expires_at)}` : ' · permanent'}
                </div>
              )}
              {!p.granted && expired && (
                <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>Expired {formatUsDate(p.expires_at)}</div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {!p.granted && (
                <input
                  type="number" min={1} placeholder="days"
                  value={daysById[p.id] ?? ''}
                  onChange={e => setDaysById(d => ({ ...d, [p.id]: e.target.value }))}
                  style={{ width: 64, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border, #ccc)', fontSize: 13 }}
                  aria-label="Access duration in days (blank = permanent)"
                />
              )}
              <button
                onClick={() => (p.granted ? revoke(p) : allow(p))}
                disabled={busyId === p.id}
                style={{
                  padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                  cursor: busyId === p.id ? 'default' : 'pointer',
                  border: '1px solid var(--color-border, #ccc)',
                  background: p.granted ? 'var(--color-surface, #fff)' : 'var(--color-primary, #2563eb)',
                  color: p.granted ? 'var(--color-text)' : '#fff',
                }}
              >
                {busyId === p.id ? '…' : (p.granted ? 'Disable' : 'Enable')}
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
